import React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import loadable from '@loadable/component';
import pMinDelay from 'p-min-delay';
import { CircularProgress } from '@mui/material';
import PrivateRoute from './PrivateRoute';
import { loadTime } from 'appConstants';
import { path } from './path';
import PropTypes from 'prop-types';
import NotFound from 'views/NotFound/NotFound';

const Dashboard = loadable(() => pMinDelay(import('views/Dashboard'), loadTime), {
  fallback: <CircularProgress />,
});

const Login = loadable(() => pMinDelay(import('views/Login'), loadTime), {
  fallback: <CircularProgress />,
});

const ForgotPassword = loadable(() => pMinDelay(import('views/ForgotPassword'), loadTime), {
  fallback: <CircularProgress />,
});

const ExpiredPassword = loadable(() => pMinDelay(import('components/ExpiredPassword'), loadTime), {
  fallback: <CircularProgress />,
});

const StarredCandidates = loadable(() => pMinDelay(import('components/Starred'), loadTime), {
  fallback: <CircularProgress />,
});

const Candidates = loadable(() => pMinDelay(import('components/Candidates'), loadTime), {
  fallback: <CircularProgress />,
});

const CandidateDetail = loadable(() => pMinDelay(import('components/CandidateDetail'), loadTime), {
  fallback: <CircularProgress />,
});

const AddCampaignToCandidate = loadable(
  () => pMinDelay(import('components/AddCampaignToCandidate'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const CandidateProfile = loadable(
  () => pMinDelay(import('components/CandidateProfile'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const Tags = loadable(() => pMinDelay(import('views/Tags'), loadTime), {
  fallback: <CircularProgress />,
});

const TagDetails = loadable(() => pMinDelay(import('views/TagDetails'), loadTime), {
  fallback: <CircularProgress />,
});

const Users = loadable(() => pMinDelay(import('components/Users'), loadTime), {
  fallback: <CircularProgress />,
});

const UserDetails = loadable(() => pMinDelay(import('components/UserDetails'), loadTime), {
  fallback: <CircularProgress />,
});

const Reporting = loadable(() => pMinDelay(import('components/Reporting'), loadTime), {
  fallback: <CircularProgress />,
});

const Downloads = loadable(() => pMinDelay(import('components/Downloads'), loadTime), {
  fallback: <CircularProgress />,
});

const Teams = loadable(() => pMinDelay(import('components/Teams'), loadTime), {
  fallback: <CircularProgress />,
});

const AddTeam = loadable(() => pMinDelay(import('components/AddTeam'), loadTime), {
  fallback: <CircularProgress />,
});

const TeamDetails = loadable(() => pMinDelay(import('components/TeamDetails'), loadTime), {
  fallback: <CircularProgress />,
});

const EditTeam = loadable(() => pMinDelay(import('components/EditTeam'), loadTime), {
  fallback: <CircularProgress />,
});

const EditTeamUsers = loadable(() => pMinDelay(import('components/EditTeamUsers'), loadTime), {
  fallback: <CircularProgress />,
});

const AddTeamUsers = loadable(() => pMinDelay(import('components/AddTeamUsers'), loadTime), {
  fallback: <CircularProgress />,
});

const Campaigns = loadable(() => pMinDelay(import('components/Campaigns'), loadTime), {
  fallback: <CircularProgress />,
});

const Profile = loadable(() => pMinDelay(import('components/Profile'), loadTime), {
  fallback: <CircularProgress />,
});

const Settings = loadable(() => pMinDelay(import('components/Settings'), loadTime), {
  fallback: <CircularProgress />,
});

const Inbox = loadable(() => pMinDelay(import('views/Inbox'), loadTime), {
  fallback: <CircularProgress />,
});

const jobChat = loadable(() => pMinDelay(import('components/jobChat'), loadTime), {
  fallback: <CircularProgress />,
});

const CampaignDetails = loadable(() => pMinDelay(import('components/CampaignDetail'), loadTime), {
  fallback: <CircularProgress />,
});

const AddCandidateToCampaign = loadable(
  () => pMinDelay(import('components/AddCandidateToCampaign'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const AddCandidate = loadable(() => pMinDelay(import('components/AddCandidate'), loadTime), {
  fallback: <CircularProgress />,
});

const ATSCandidate = loadable(() => pMinDelay(import('components/ATSCandidate'), loadTime), {
  fallback: <CircularProgress />,
});

const AddUser = loadable(() => pMinDelay(import('components/AddUser'), loadTime), {
  fallback: <CircularProgress />,
});

const AddCampaign = loadable(
  () => pMinDelay(import('components/Campaigns/AddCampaign'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const Calendar = loadable(() => pMinDelay(import('views/Calendar'), loadTime), {
  fallback: <CircularProgress />,
});

const ScheduleMessage = loadable(() => pMinDelay(import('components/ScheduleMessage'), loadTime), {
  fallback: <CircularProgress />,
});

const InBoundSettings = loadable(
  () => pMinDelay(import('components/Settings/InBoundApplications'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);
const icimsIntegrations = loadable(
  () => pMinDelay(import('components/Settings/IcimsIntegrations'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const emailIntegration = loadable(
  () => pMinDelay(import('components/Settings/EmailIntegration'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const greenhouseIntegeration = loadable(
  () => pMinDelay(import('components/Settings/GreenHouseIntegration'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const bullhornIntegration = loadable(
  () => pMinDelay(import('components/Settings/BullhornIntegration'), loadTime),
  {
    fallback: <CircularProgress />,
  },
);

const Sso = loadable(() => pMinDelay(import('views/Sso'), loadTime), {
  fallback: <CircularProgress />,
});

const RegulatoryBundle = loadable(() => pMinDelay(import('views/RegulatoryBundle'), loadTime), {
  fallback: <CircularProgress />,
});

const Router = ({ authenticated }) => (
  <Switch>
    <Route
      exact
      path={path.HOME}
      render={() => (
        <Redirect
          to={{
            pathname: authenticated ? path.DASHBOARD : path.LOGIN,
          }}
        />
      )}
    />
    <Route
      exact
      path={[path.LOGIN, path.LOGIN_REASON, path.SSO_LOGIN, path.IAM]}
      component={Login}
    />
    <Route exact path={path.SSO_TID} component={Sso} />
    <Route
      exact
      path={[path.FORGOT_PW, path.FORGOT_PW_INVALID_CODE, path.FORGOT_PW_REQ, path.SEND_INVITE]}
      component={ForgotPassword}
    />
    <Route
      exact
      path={path.CALENDAR_CONNECTED}
      render={() => (
        <Redirect
          to={{
            pathname: path.SETTINGS,
            state: { isCalendarConnected: true },
          }}
        />
      )}
    />
    <Route exact path={path.EXPIRED_PW} component={ExpiredPassword} />
    <PrivateRoute exact path={path.DASHBOARD} component={Dashboard} authenticated={authenticated} />
    <PrivateRoute
      exact
      path={path.STARRED}
      component={StarredCandidates}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.CANDIDATES}
      component={Candidates}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.CANDIDATES_ADD}
      component={AddCandidate}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.CANDIDATES_EDIT}
      component={AddCandidate}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.CANDIDATE_DETAIL}
      component={CandidateDetail}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.INBOX_CANDIDATE_ID}
      component={CandidateProfile}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.ATS_CANDIDATE}
      component={ATSCandidate}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.ADD_CAMPAIGN_TO_CANDIDATE}
      component={AddCampaignToCandidate}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.REGULATORY_BUNDLES}
      component={RegulatoryBundle}
      authenticated={authenticated}
    />
    <PrivateRoute exact path={path.TAGS} component={Tags} authenticated={authenticated} />
    <PrivateRoute exact path={path.TAG} component={TagDetails} authenticated={authenticated} />
    <PrivateRoute exact path={path.USERS} component={Users} authenticated={authenticated} />
    <PrivateRoute exact path={path.USERS_ADD} component={AddUser} authenticated={authenticated} />
    <PrivateRoute exact path={path.USER} component={UserDetails} authenticated={authenticated} />
    <PrivateRoute exact path={path.USERS_EDIT} component={AddUser} authenticated={authenticated} />
    <PrivateRoute exact path={path.DOWNLOADS} component={Downloads} authenticated={authenticated} />
    <PrivateRoute exact path={path.TEAM} component={TeamDetails} authenticated={authenticated} />
    <PrivateRoute exact path={path.TEAMS} component={Teams} authenticated={authenticated} />
    <PrivateRoute exact path={path.EDIT_TEAM} component={EditTeam} authenticated={authenticated} />
    <PrivateRoute
      exact
      path={path.EDIT_TEAM_USERS}
      component={EditTeamUsers}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.ADD_TEAM_USERS}
      component={AddTeamUsers}
      authenticated={authenticated}
    />
    <PrivateRoute exact path={path.ADD_TEAM} component={AddTeam} authenticated={authenticated} />
    <PrivateRoute exact path={path.REPORTING} component={Reporting} authenticated={authenticated} />
    <PrivateRoute exact path={path.CAMPAIGNS} component={Campaigns} authenticated={authenticated} />
    <PrivateRoute
      exact
      path={path.CAMPAIGN}
      component={CampaignDetails}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.CAMPAIGN_DETAIL_ON_NO_NAV_MODE}
      component={CampaignDetails}
      authenticated={authenticated}
    />
    <PrivateRoute exact path={path.SETTINGS} component={Settings} authenticated={authenticated} />
    <PrivateRoute exact path={path.PROFILE} component={Profile} authenticated={authenticated} />
    <PrivateRoute exact path={path.INBOX_ID} component={Inbox} authenticated={authenticated} />
    <PrivateRoute exact path={path.JOB_CHAT} component={jobChat} authenticated={authenticated} />
    <PrivateRoute
      exact
      path={path.ADD_CANDIDATE_TO_CAMPAIGN}
      component={AddCandidateToCampaign}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.ADD_CAMPAIGN}
      component={AddCampaign}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.EDIT_CAMPAIGN}
      component={AddCampaign}
      authenticated={authenticated}
    />
    <PrivateRoute exapt path={path.CALENDAR} component={Calendar} authenticated={authenticated} />
    <PrivateRoute exapt path={path.EVENTS} component={Calendar} authenticated={authenticated} />
    <PrivateRoute
      exact
      path={path.PAGE_SCHEDULE_MESSAGE}
      component={ScheduleMessage}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SCHEDULE_MESSAGE_WITH_ID}
      component={ScheduleMessage}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SCHEDULE_MESSAGE_EDIT}
      component={ScheduleMessage}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SETTINGS_INBOUND_APPLICATIONS}
      component={InBoundSettings}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SETTINGS_ICIMS_INTEGRATIONS}
      component={icimsIntegrations}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SETTINGS_EMAIL_INTEGRATIONS}
      component={emailIntegration}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SETTINGS_GREENHOUSE_INTEGRATIONS}
      component={greenhouseIntegeration}
      authenticated={authenticated}
    />
    <PrivateRoute
      exact
      path={path.SETTINGS_BULLHORN_INTEGRATIONS}
      component={bullhornIntegration}
      authenticated={authenticated}
    />
    <Route path="*">
      <NotFound />
    </Route>
  </Switch>
);

Router.propTypes = {
  authenticated: PropTypes.bool.isRequired,
};

export default Router;
